import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { guardarCertificadoPermisoService } from "../../../Service/Service.certificados";
import { createPDFpermisos } from "../../../Service/Service.certificadosPDF";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import { hcError, hcExito } from "../hcSwal";
import HCModal from "../HCModal";
import { DateBadge, Field, OrderBadge, PrintPreview, Section, Segmented } from "../HCPiezas";
import TabDiagnosticos from "./TabDiagnosticos";

/* ============================================================================
   Certificado de permiso / reposo laboral — migrado a HCModal.

   Payload y endpoint idénticos: POST /certificado_permiso_orden.
   `tipo_contingencia` se queda como texto libre: es una columna de prosa, no
   hay tabla de catálogo y el cliente pidió dejarlo como estaba.

   Los tres motivos de reposo (Enfermedad · Presenta síntomas ·
   Aislamiento/teletrabajo) eran tres interruptores que se DESHABILITABAN entre
   sí: en la práctica ya era una elección única. Pasan a un segmented, que es
   esa misma regla sin estados imposibles. NO se convierten en multi-selección
   porque eso cambiaría lo que se guarda (hoy nunca hubo dos a la vez).

   Ojo: `enfermedad` no tiene columna en `certificado_orden` -- el controller no
   la lee. Se manda igual que antes para no cambiar el payload, pero no
   persiste, y la UI lo dice.

   `fecha_ini` / `fecha_fin` estaban en el estado inicial del modal viejo y no
   se usaban en ningún sitio: la UI escribe `fecha_desde` / `fecha_hasta`, que
   son las que lee el backend. Se eliminan por muertas.
   ========================================================================== */

const MOTIVOS = [
  { value: "enfermedad", label: "Enfermedad" },
  { value: "presenta_sintomas", label: "Presenta síntomas" },
  { value: "aislamiento_teletrabajo", label: "Aislamiento / teletrabajo" },
];

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaCertificado: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  idclinica: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  enfermedad: false,
  presenta_sintomas: false,
  aislamiento_teletrabajo: false,
  descripcion_enfermedad: "",
  tipo_contingencia: "",
  empresa_destino: "",
  fecha_desde: "",
  fecha_hasta: "",
  dias_descanso: 0,
  estado: "A",
};

const dias = (desde, hasta) => {
  if (!desde || !hasta) return 0;
  const d = moment(hasta).diff(moment(desde), "days") + 1;
  return d > 0 ? d : 0;
};

export default function ModalPermisoHC({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  IdCategoriaExamen,
  ListaCitaPaciente,
}) {
  const userLocal = GetTokenDecoded();
  const [tab, setTab] = useState("permiso");
  const [form, setForm] = useState(FORM_VACIO);
  const [motivo, setMotivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const desdeRef = useRef(null);

  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      idclinica: userLocal?.idclinica ?? 0,
      IdCategoriaExamen,
      numero_orden: `PER-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
    });
    setMotivo(null);
    setIntentado(false);
    setTab("permiso");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cita, IdCategoriaExamen]);

  // Un solo motivo activo, igual que hoy: los otros dos se mandan en false.
  const elegirMotivo = (v) => {
    setMotivo(v);
    setForm((f) => ({
      ...f,
      enfermedad: v === "enfermedad",
      presenta_sintomas: v === "presenta_sintomas",
      aislamiento_teletrabajo: v === "aislamiento_teletrabajo",
    }));
  };

  const setFecha = (campo) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [campo]: v };
      // El fin nunca antes del inicio: se arrastra al inicio en vez de dejar un
      // periodo imposible.
      if (next.fecha_desde && next.fecha_hasta && next.fecha_hasta < next.fecha_desde) {
        if (campo === "fecha_desde") next.fecha_hasta = next.fecha_desde;
        else next.fecha_hasta = next.fecha_desde;
      }
      next.dias_descanso = dias(next.fecha_desde, next.fecha_hasta);
      return next;
    });
  };

  const faltantes = useMemo(() => {
    const f = [];
    if (!motivo) f.push("Motivo del reposo");
    if (!form.fecha_desde) f.push("Desde");
    if (!form.fecha_hasta) f.push("Hasta");
    return f;
  }, [motivo, form.fecha_desde, form.fecha_hasta]);

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      desdeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    const data = await guardarCertificadoPermisoService(form);
    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      hcError("No se pudo guardar", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }
    hcExito("Certificado de permiso emitido", `${form.dias_descanso} día(s) de reposo.`);
    setLoading(false);
    // "Emitir e imprimir": el PDF se abre con el id que acaba de devolver.
    const id = data?.data?.idcertificado_orden;
    if (id) await createPDFpermisos(id);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    setMotivo(null);
    onHide && onHide();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const nombre = paciente?.nombres_completos || cita?.pacientes?.nombres_completos || "el paciente";
  const subtitulo = [
    nombre,
    paciente?.edad ? `${paciente.edad} a` : null,
    paciente?.cedula ? `CI ${paciente.cedula}` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const etiquetaMotivo = MOTIVOS.find((m) => m.value === motivo)?.label;

  return (
    <HCModal
      isOpen={!!show}
      size="lg"
      title="Certificado de permiso / reposo laboral"
      subtitle={subtitulo}
      meta={[
        <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de emisión" />,
        <OrderBadge key="o" code={form.numero_orden} label="N.º de certificado" />,
      ]}
      tabs={[
        { key: "permiso", label: "Permiso" },
        { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      validation={{ missing: faltantes }}
      primaryLabel="Emitir e imprimir"
      onPrimary={guardar}
      primaryLoading={loading}
      onClose={cerrar}
      onCancel={cerrar}
      hayCambiosSinGuardar={Boolean(motivo || form.fecha_desde || form.descripcion_enfermedad || form.empresa_destino)}
    >
      {tab === "permiso" ? (
        <>
          <Section
            title="1 · Motivo del reposo"
            hint="Uno solo, como hasta ahora"
          >
            <Segmented ariaLabel="Motivo del reposo" options={MOTIVOS} value={motivo} onChange={elegirMotivo} />
            {intentado && !motivo && (
              <div className="hcf__error" style={{ marginTop: 7 }} role="alert">
                Elige el motivo del reposo
              </div>
            )}
            {motivo === "enfermedad" && (
              <div className="hcf__hint" style={{ marginTop: 7 }}>
                Nota: «Enfermedad» no tiene columna propia en el certificado; se registra
                mediante la descripción de abajo.
              </div>
            )}
          </Section>

          <Section title="2 · Destinatario">
            <div className="hcgrid hcgrid--2">
              <Field label="Empresa o institución a quien se dirige" htmlFor="hc-per-empresa">
                <input
                  id="hc-per-empresa"
                  type="text"
                  placeholder="Nombre de la empresa"
                  value={form.empresa_destino}
                  onChange={(e) => setForm({ ...form, empresa_destino: e.target.value })}
                />
              </Field>
              <Field
                label="Tipo de contingencia"
                htmlFor="hc-per-conting"
                hint="Texto libre (no hay catálogo en el sistema)"
              >
                <input
                  id="hc-per-conting"
                  type="text"
                  placeholder="Enfermedad general, accidente de trabajo…"
                  value={form.tipo_contingencia}
                  onChange={(e) => setForm({ ...form, tipo_contingencia: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="3 · Periodo de reposo">
            <div className="hcgrid hcgrid--3" ref={desdeRef}>
              <Field
                label="Desde"
                required
                htmlFor="hc-per-desde"
                error={intentado && !form.fecha_desde ? "Obligatorio" : null}
              >
                <input
                  id="hc-per-desde"
                  type="date"
                  value={form.fecha_desde}
                  aria-invalid={intentado && !form.fecha_desde ? "true" : undefined}
                  onChange={setFecha("fecha_desde")}
                />
              </Field>
              <Field
                label="Hasta"
                required
                htmlFor="hc-per-hasta"
                hint={form.fecha_desde ? "No puede ser anterior al inicio" : null}
                error={intentado && !form.fecha_hasta ? "Obligatorio" : null}
              >
                <input
                  id="hc-per-hasta"
                  type="date"
                  min={form.fecha_desde || undefined}
                  value={form.fecha_hasta}
                  aria-invalid={intentado && !form.fecha_hasta ? "true" : undefined}
                  onChange={setFecha("fecha_hasta")}
                />
              </Field>
              <Field label="Días de descanso" hint="Calculado, incluye ambos extremos">
                <div className="hcdias" aria-live="polite">
                  {form.dias_descanso || "—"}
                  <span>{form.dias_descanso === 1 ? "día" : "días"}</span>
                </div>
              </Field>
            </div>
          </Section>

          <Section title="4 · Descripción">
            <Field label="Descripción de la enfermedad o condición" htmlFor="hc-per-desc">
              <textarea
                id="hc-per-desc"
                rows={3}
                placeholder="Lo que se imprime como justificación del reposo"
                value={form.descripcion_enfermedad}
                onChange={(e) => setForm({ ...form, descripcion_enfermedad: e.target.value })}
              />
            </Field>

            <PrintPreview>
              Se certifica que <b>{nombre}</b> requiere reposo
              {etiquetaMotivo ? ` por ${etiquetaMotivo.toLowerCase()}` : ""}
              {form.fecha_desde && form.fecha_hasta
                ? ` desde el ${moment(form.fecha_desde).format("DD/MM/YYYY")} hasta el ${moment(
                    form.fecha_hasta
                  ).format("DD/MM/YYYY")}, ${form.dias_descanso} ${form.dias_descanso === 1 ? "día" : "días"}`
                : ""}
              .{form.descripcion_enfermedad ? ` ${form.descripcion_enfermedad}` : ""}
              {form.empresa_destino ? ` Dirigido a ${form.empresa_destino}.` : ""}
            </PrintPreview>
          </Section>
        </>
      ) : (
        <TabDiagnosticos diagnosticos={diagnosticos} onRefrescar={ListaCitaPaciente} />
      )}
    </HCModal>
  );
}
